import { Form } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { ReactNode } from 'react'
import { useModalWizardStepsContext } from './ModalWizardStepsContext'
import { ModalWizard, ModalWizardProps } from '../ModalWizard'
import { StepsComponent } from '../StepsComponent'
import ButtonLoading from '@/components/misc/Button'

interface IStep {
  name: string
  actionButtons: {
    label: string
    secondaryAction?: () => void
    primaryAction?: (_data: unknown) => void
  }[]
  component: ReactNode
}

interface IModalWizzardStepsProps
  extends Omit<ModalWizardProps, 'footer' | 'onClose'> {
  title?: string
  subtitle?: string
  currentStep: number
  isOpen: boolean
  onClose: (_cancelled: boolean) => void
  steps: IStep[]
  size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'
  modalClassName?: string
  isSending: boolean
  confirmOnClose?: boolean
}

export function ModalWizardSteps({
  currentStep,
  steps,
  isOpen,
  onClose,
  title,
  subtitle,
  size,
  modalClassName,
  isSending,
  confirmOnClose = false,
}: IModalWizzardStepsProps) {
  const { useFormWizzard } = useModalWizardStepsContext()

  const footer = (
    <Footer isSending={isSending} onClose={onClose} step={steps[currentStep]} />
  )

  return (
    <ModalWizard
      confirmOnClose={confirmOnClose}
      footer={footer}
      isOpen={isOpen}
      modalClassName={modalClassName}
      onClose={onClose}
      size={size}
      subtitle={subtitle}
      title={title}
    >
      <Form {...useFormWizzard}>
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex-shrink-0">
            <StepsComponent
              currentStep={currentStep}
              steps={steps.map((item) => item.name)}
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-2 pt-2 scrollbar-thin">
            {steps[currentStep].component}
          </div>
        </div>
      </Form>
    </ModalWizard>
  )
}

interface IFooterProps {
  step: IStep
  onClose: (_cancelled: boolean) => void
  isSending: boolean
}

function Footer({ step, onClose, isSending }: IFooterProps) {
  const { useFormWizzard } = useModalWizardStepsContext()
  const { handleSubmit } = useFormWizzard

  return (
    <>
      <div className="flex w-full items-center justify-start">
        <Button
          className="w-full sm:w-auto"
          disabled={isSending}
          onClick={() => onClose(false)}
          variant="outline"
        >
          Cancelar
        </Button>
      </div>
      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
        {step.actionButtons.map((btn, index) =>
          isSending && index === 1 ? (
            <ButtonLoading
              key={`loading-${index}`}
              loading={isSending}
              name="Guardando..."
            />
          ) : (
            <Button
              className="w-full sm:w-auto"
              disabled={isSending}
              key={`button-${index}-${btn.label}`}
              onClick={
                btn.primaryAction
                  ? handleSubmit(btn.primaryAction)
                  : btn.secondaryAction
              }
              variant={
                index === step.actionButtons.length - 1 ? 'default' : 'outline'
              }
            >
              {btn.label}
            </Button>
          )
        )}
      </div>
    </>
  )
}
